import React, { useState, useEffect } from 'react';
import { X, ScanLine } from 'lucide-react';
import api, { documentAPI } from '../../../api.js';
import { useTranslation } from 'react-i18next';
import { useOrganizations } from '../../../index.js';
import { useQuery } from '@tanstack/react-query';
import { workingSiteAPI } from '../../../api.js';
import { useAuth } from '../../../v3/context/AuthContext.jsx';
import { DocumentScanner, SCAN_CONFIGS } from '../../document-scanner/index.js';
import PrivacyNoticeModal from '../modals/PrivacyNoticeModal.jsx';
import {
  parseLicenseCategories, categoriesToText, categoriesToBooleans,
  textToCategories, syncBooleansIntoCategories,
} from '../../../utils/driverLicense.js';

function DriverEditModal({ isOpen, onClose, driver, onSuccess, onMessage }) {
  const { t } = useTranslation();
  const { data: organizations = [] } = useOrganizations();
  const { user } = useAuth();

  // Resolve user's org_id: use primary_org_id from user profile (this is the org CODE, e.g. "ORG-512539")
  const userOrgId = user?.primary_org_id || '';

  // Fetch sites for site assignment dropdown
  const { data: sitesData } = useQuery({
    queryKey: ['working-sites'],
    queryFn: () => workingSiteAPI.getAll(),
    enabled: isOpen
  });

  const [noticeForNewDriver, setNoticeForNewDriver] = useState(null);

  const [formData, setFormData] = useState({
    first_name: driver?.first_name || '',
    last_name: driver?.last_name || '',
    email: driver?.email || '',
    phone: driver?.phone || '',
    license_number: driver?.license_number || '',
    license_issue_date: driver?.license_issue_date || '',
    license_expiry: driver?.license_expiry || '',
    birth_date: driver?.birth_date || '',
    address: driver?.address || '',
    status: driver?.status || 'active',
    org_id: driver?.org_id || userOrgId,
    working_site_id: driver?.working_site_id || '',
    license_country: driver?.license_country || '',
    license_categories: driver?.license_categories || '',
    license_category_b: driver?.license_category_b || false,
    license_category_c: driver?.license_category_c || false,
    license_category_d: driver?.license_category_d || false,
    license_category_ce: driver?.license_category_ce || false,
    cert_adr: driver?.cert_adr || false,
    cert_fimo: driver?.cert_fimo || false,
    cert_crane: driver?.cert_crane || false,
    cert_forklift: driver?.cert_forklift || false,
    last_verification_date: driver?.last_verification_date || '',
    verification_frequency: driver?.verification_frequency || 'Annuelle (Recommandé)',
  });

  useEffect(() => {
    if (driver) {
      setFormData({
        first_name: driver.first_name || '',
        last_name: driver.last_name || '',
        email: driver.email || '',
        phone: driver.phone || '',
        license_number: driver.license_number || '',
        license_issue_date: driver.license_issue_date || '',
        license_expiry: driver.license_expiry || '',
        birth_date: driver.birth_date || '',
        address: driver.address || '',
        status: driver.status || 'active',
        org_id: driver.org_id || userOrgId,
        working_site_id: driver.working_site_id || '',
        license_country: driver.license_country || '',
        license_categories: driver.license_categories || '',
        license_category_b: driver.license_category_b || false,
        license_category_c: driver.license_category_c || false,
        license_category_d: driver.license_category_d || false,
        license_category_ce: driver.license_category_ce || false,
        cert_adr: driver.cert_adr || false,
        cert_fimo: driver.cert_fimo || false,
        cert_crane: driver.cert_crane || false,
        cert_forklift: driver.cert_forklift || false,
        last_verification_date: driver.last_verification_date || '',
        verification_frequency: driver.verification_frequency || 'Annuelle (Recommandé)',
      });
    }
  }, [driver, userOrgId]);

  const [showScanner, setShowScanner] = useState(false);
  const [pendingScanFile, setPendingScanFile] = useState(null);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Convert DD/MM/YYYY (OCR output) to YYYY-MM-DD (input[type=date])
  const parseOcrDate = (str) => {
    if (!str || str === '???') return '';
    const m = str.match(/(\d{2})[/.:-](\d{2})[/.:-](\d{4})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };

  // Map scanned license data to driver form fields + auto-upload document
  const handleScanComplete = async ({ data, file }) => {
    // Map OCR data to form fields (same mapping as before)
    setFormData(prev => ({
      ...prev,
      ...(data.nom && data.nom !== '???' ? { last_name: data.nom } : {}),
      ...(data.prenom && data.prenom !== '???' ? { first_name: data.prenom } : {}),
      ...(data.numero_permis && data.numero_permis !== '???' ? { license_number: data.numero_permis } : {}),
      ...(data.date_obtention ? { license_issue_date: parseOcrDate(data.date_obtention) } : {}),
      ...(data.date_expiration ? { license_expiry: parseOcrDate(data.date_expiration) } : {}),
      ...(data.date_naissance ? { birth_date: parseOcrDate(data.date_naissance) } : {}),
      // Catégories lues sur le permis scanné : parser UE partagé (2006/126/CE).
      ...(data.categories && data.categories !== '???' ? (() => {
        const cats = parseLicenseCategories(data.categories).categories;
        return { license_categories: categoriesToText(cats), ...categoriesToBooleans(cats) };
      })() : {}),
    }));
    setShowScanner(false);

    // Build declared_data matching VALIDATION_FIELDS.DRIVING_LICENCE codes
    const declaredData = {};
    if (data.numero_permis && data.numero_permis !== '???') declaredData.license_number = data.numero_permis;
    if (data.prenom || data.nom) {
      const parts = [data.prenom, data.nom].filter(p => p && p !== '???');
      if (parts.length) declaredData.holder_name = parts.join(' ');
    }
    if (data.date_naissance && data.date_naissance !== '???') declaredData.date_of_birth = parseOcrDate(data.date_naissance);
    if (data.categories && data.categories !== '???') declaredData.categories = data.categories;
    if (data.date_expiration && data.date_expiration !== '???') declaredData.expiry_date = parseOcrDate(data.date_expiration);
    if (data.date_obtention && data.date_obtention !== '???') declaredData.obtained_date = parseOcrDate(data.date_obtention);

    if (!file) return;

    const isEditMode = !!driver?.id;
    if (isEditMode) {
      // Upload immediately for existing driver
      const referenceId = `DRV_${String(driver.id).padStart(6, '0')}`;
      try {
        await documentAPI.upload(file, {
          scope: 'DRIVER',
          reference_id: referenceId,
          document_type: 'DRIVING_LICENCE',
          valid_to: parseOcrDate(data.date_expiration) || undefined,
          declared_data: JSON.stringify(declaredData),
        });
        onMessage?.({ title: t('drivers.form.documentTitle'), message: t('drivers.form.licenceSaved'), type: 'success' });
      } catch (err) {
        console.error('Auto-upload failed:', err);
        onMessage?.({ title: t('drivers.form.documentTitle'), message: t('drivers.form.licenceSaveFailed'), type: 'error' });
      }
    } else {
      // Defer upload until driver is created
      setPendingScanFile({
        file,
        declaredData,
        validTo: parseOcrDate(data.date_expiration) || undefined,
      });
    }
  };

  const handleSubmit = async () => {
    const isEditMode = !!driver?.id;

    // Validate required fields
    const requiredFields = [
      { field: 'first_name', label: t('drivers.detail.info.firstName') },
      { field: 'last_name', label: t('drivers.detail.info.lastName') },
      { field: 'email', label: t('drivers.detail.info.email') },
      { field: 'license_number', label: t('drivers.form.licenseNumber') },
      { field: 'license_expiry', label: t('drivers.form.licenseExpiry') },
      { field: 'org_id', label: t('drivers.columns.organization') }
    ];

    const missingFields = requiredFields.filter(({ field }) => !formData[field] || formData[field].trim() === '');

    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(({ label }) => label).join(', ');
      onMessage && onMessage({
        title: t('drivers.form.validationError'),
        message: t('drivers.form.requiredFields', { fields: fieldNames }),
        type: 'error'
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      onMessage && onMessage({
        title: t('drivers.form.validationError'),
        message: t('drivers.form.invalidEmail'),
        type: 'error'
      });
      return;
    }

    try {
      // Convert boolean values to integers (0 or 1) for database
      const submitData = {
        ...formData,
        working_site_id: formData.working_site_id || null,
        license_category_b: formData.license_category_b ? 1 : 0,
        license_category_c: formData.license_category_c ? 1 : 0,
        license_category_d: formData.license_category_d ? 1 : 0,
        license_category_ce: formData.license_category_ce ? 1 : 0,
        cert_adr: formData.cert_adr ? 1 : 0,
        cert_fimo: formData.cert_fimo ? 1 : 0,
        cert_crane: formData.cert_crane ? 1 : 0,
        cert_forklift: formData.cert_forklift ? 1 : 0,
        // Le champ texte `license_categories` fait foi (15 catégories UE) ;
        // ce formulaire n'expose que 4 cases : on y reporte leur état SANS
        // perdre les autres catégories déjà saisies (C1E, D1, AM…).
        license_categories: categoriesToText(
          syncBooleansIntoCategories(textToCategories(formData.license_categories), formData)
        ),
      };

      if (isEditMode) {
        // Update existing driver
        await api.driver.update(driver.id, submitData);
        onMessage && onMessage({
          title: t('drivers.messages.successTitle'),
          message: t('drivers.form.updated'),
          type: 'success'
        });
      } else {
        // Create new driver
        const createResult = await api.driver.create(submitData);
        onMessage && onMessage({
          title: t('drivers.messages.successTitle'),
          message: t('drivers.form.created'),
          type: 'success'
        });

        // Upload pending scan file if exists
        if (pendingScanFile?.file && createResult?.data?.id) {
          const newDriverId = createResult.data.id;
          const referenceId = `DRV_${String(newDriverId).padStart(6, '0')}`;
          try {
            await documentAPI.upload(pendingScanFile.file, {
              scope: 'DRIVER',
              reference_id: referenceId,
              document_type: 'DRIVING_LICENCE',
              valid_to: pendingScanFile.validTo,
              declared_data: JSON.stringify(pendingScanFile.declaredData),
            });
          } catch (err) {
            console.error('Post-create upload failed:', err);
          }
          setPendingScanFile(null);
        }

        // Show RGPD notice modal for new drivers — Art. 13 RGPD compliance.
        // The driver record is created but acknowledgment is captured next
        // (signature in privacy_notice_acknowledgments). Skip if no driver id
        // returned (unexpected backend response).
        if (createResult?.data?.id) {
          setNoticeForNewDriver({ ...submitData, id: createResult.data.id });
          return; // defer onSuccess/onClose until notice flow completes
        }
      }
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} driver:`, error);
      onMessage && onMessage({
        title: t('drivers.messages.errorTitle'),
        message: isEditMode ? t('drivers.form.updateError', { error: error.message }) : t('drivers.form.createError', { error: error.message }),
        type: 'error'
      });
    }
  };

  if (!isOpen) return null;

  const isEditMode = !!driver?.id;

  // Full-screen scanner overlay
  if (showScanner) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full h-[90vh] max-w-7xl overflow-hidden">
          <DocumentScanner
            config={SCAN_CONFIGS.permis_conduire}
            onComplete={handleScanComplete}
            onCancel={() => setShowScanner(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">{isEditMode ? t('drivers.form.editTitle') : t('drivers.form.addTitle')}</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowScanner(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-sm font-semibold transition-colors shadow-lg"
            >
              <ScanLine className="w-4 h-4" />
              {t('drivers.form.scanLicence')}
            </button>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-form="driver-info">
          <h3 className="font-semibold text-gray-900">Détails du Conducteur</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.info.firstName')}</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => updateFormData('first_name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.info.lastName')}</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => updateFormData('last_name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.info.email')} *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.info.phone')}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.form.licenseNumber')} *</label>
              <input
                type="text"
                value={formData.license_number}
                onChange={(e) => updateFormData('license_number', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.form.licenseIssueDate')}</label>
              <input
                type="date"
                value={formData.license_issue_date}
                onChange={(e) => updateFormData('license_issue_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.form.licenseExpiry')} *</label>
              <input
                type="date"
                value={formData.license_expiry}
                onChange={(e) => updateFormData('license_expiry', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.info.birthDate')}</label>
              <input
                type="date"
                value={formData.birth_date}
                onChange={(e) => updateFormData('birth_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.info.address')}</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateFormData('address', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.columns.status')}</label>
              <select
                value={formData.status}
                onChange={(e) => updateFormData('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="active">{t('drivers.status.active')}</option>
                <option value="temporary">{t('drivers.status.temporary')}</option>
                <option value="interim">{t('drivers.status.interim')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.columns.organization')} *</label>
              {organizations.length <= 1 ? (
                // Single org: show as read-only (no risk of selecting wrong tenant)
                <input
                  type="text"
                  value={organizations[0]?.name || userOrgId || '—'}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                  readOnly
                />
              ) : (
                <select
                  value={formData.org_id}
                  onChange={(e) => updateFormData('org_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">{t('drivers.form.selectOrganization')}</option>
                  {organizations.map(org => (
                    <option key={org.code} value={org.code}>
                      {org.name} ({org.code})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Site Assignment */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-green-900">{t('drivers.form.homeSite')}</h4>
            <p className="text-sm text-green-800">{t('drivers.form.homeSiteHint')}</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.site.title')}</label>
              <select
                value={formData.working_site_id || ''}
                onChange={(e) => updateFormData('working_site_id', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">{t('drivers.form.noSite')}</option>
                {(sitesData?.data || [])
                  .filter(site => !formData.org_id || site.org_id === formData.org_id)
                  .map(site => (
                    <option key={site.id} value={site.id}>
                      {site.site_name} - {site.site_type} ({site.city || 'N/A'})
                    </option>
                  ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                {t('drivers.form.siteStatsHint')}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-blue-900">{t('drivers.detail.license.categories')}</h4>
            <p className="text-sm text-blue-800">{t('drivers.form.categoriesHint')}</p>

            {/* Pays de délivrance — ISO 3166-1 alpha-2 (permis UE harmonisé). */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.license.country')}</label>
              <input
                type="text"
                value={formData.license_country}
                onChange={(e) => updateFormData('license_country', e.target.value.toUpperCase().slice(0, 2))}
                placeholder="FR"
                maxLength={2}
                className="w-28 px-4 py-2 border border-gray-300 rounded-lg uppercase"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.license_category_b}
                  onChange={(e) => updateFormData('license_category_b', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.categoryB')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.license_category_c}
                  onChange={(e) => updateFormData('license_category_c', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.categoryC')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.license_category_d}
                  onChange={(e) => updateFormData('license_category_d', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.categoryD')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.license_category_ce}
                  onChange={(e) => updateFormData('license_category_ce', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.categoryCE')}</span>
              </label>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-purple-900">{t('drivers.detail.license.certifications')}</h4>
            <p className="text-sm text-purple-800">{t('drivers.form.certificationsHint')}</p>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.cert_adr}
                  onChange={(e) => updateFormData('cert_adr', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.certAdr')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.cert_fimo}
                  onChange={(e) => updateFormData('cert_fimo', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.certFimo')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.cert_crane}
                  onChange={(e) => updateFormData('cert_crane', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.certCrane')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.cert_forklift}
                  onChange={(e) => updateFormData('cert_forklift', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('drivers.form.certForklift')}</span>
              </label>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-yellow-900">{t('drivers.form.verificationTitle')}</h4>
            <p className="text-sm text-yellow-800">
              {t('drivers.form.verificationHint')}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.form.lastVerification')}</label>
              <input
                type="date"
                value={formData.last_verification_date}
                onChange={(e) => updateFormData('last_verification_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.license.verificationFrequency')}</label>
              <select
                value={formData.verification_frequency}
                onChange={(e) => updateFormData('verification_frequency', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Annuelle (Recommandé)">{t('drivers.form.frequency.annual')}</option>
                <option value="Semestrielle">{t('drivers.form.frequency.semiAnnual')}</option>
                <option value="Trimestrielle">{t('drivers.form.frequency.quarterly')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
          <button onClick={onClose} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400">
            {t('common.actions.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {isEditMode ? t('drivers.form.update') : t('common.actions.add')}
          </button>
        </div>
      </div>

      {/* RGPD notice modal — shown after a new driver is created (Art. 13 RGPD) */}
      <PrivacyNoticeModal
        isOpen={!!noticeForNewDriver}
        driver={noticeForNewDriver}
        onAcknowledged={(ack) => {
          onMessage && onMessage({
            title: t('drivers.form.noticeSignedTitle'),
            message: t('drivers.form.noticeSigned', { name: noticeForNewDriver?.first_name || t('drivers.form.theDriver') }),
            type: 'success',
          });
          setNoticeForNewDriver(null);
          onSuccess && onSuccess();
          onClose();
        }}
        onClose={() => {
          // Driver was created but acknowledgment was deferred — keep driver,
          // warn caller, and close. The acknowledgment can be captured later.
          onMessage && onMessage({
            title: t('drivers.form.noticeDeferredTitle'),
            message: t('drivers.form.noticeDeferred', { name: noticeForNewDriver?.first_name || t('drivers.form.theDriverCap') }),
            type: 'warning',
          });
          setNoticeForNewDriver(null);
          onSuccess && onSuccess();
          onClose();
        }}
      />
    </div>
  );
}

// Drivers List Component with License Verification
// ============================================================================
// --- DRIVER DETAIL VIEW (WITH TABS) ---

export default DriverEditModal;
